import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getShowingDefaultSettings } from 'api/UserController';
import {
  Title,
  Text,
  Switch,
  RadioGroup,
  Radio,
  Dropdown,
  theme,
  Menu,
  MenuItem,
  Checkbox,
  InputTextArea,
} from 'components';
import { Input } from 'antd';
import {
  ApproveShowingOption,
  approveShowingsOptions,
  bufferOptions,
  maxShowingDurationOptions,
  overlapOptions,
  showingAgentInstructionsOptions,
} from '@app/constants';
import { DownOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import AppointmentConfigDefault from '@bb/domain/dist/types/AppointmentConfigDefault';
import { MAX_NOTICE_IN_MINUTES, featureEnabled } from 'utils';

const DefaultShowingSettings = ({
  showingSettings,
  setShowingSettings,
}: {
  showingSettings: Partial<AppointmentConfigDefault>;
  setShowingSettings: React.Dispatch<
    React.SetStateAction<Partial<AppointmentConfigDefault>>
  >;
}) => {
  const [showVendorInstructions, setShowVendorInstructions] = useState(
    !!showingSettings?.vendor_instructions,
  );

  const [showOtherInstructions, setShowOtherInstructions] = useState(
    !!showingSettings?.instructions?.confirmation?.details,
  );

  const [noticeHours, setNoticeHours] = useState(showingSettings.notice);
  const [noticeMins, setNoticeMins] = useState(showingSettings.noticeMinutes);

  useEffect(() => {
    const { notice, noticeMinutes } = showingSettings;
    if (notice == null && noticeMinutes == null) {
      // Set default notice to 1 hour
      setNoticeHours(1);
      setShowingSettings(prev => ({ ...prev, notice: 1 }));
    } else {
      setNoticeHours(notice);
      setNoticeMins(noticeMinutes);
    }
  }, [showingSettings]);

  useEffect(() => {
    setShowOtherInstructions(
      !!showingSettings?.instructions?.confirmation?.details,
    );
    setShowVendorInstructions(!!showingSettings?.vendor_instructions);
  }, [
    showingSettings?.instructions?.confirmation?.details,
    showingSettings?.vendor_instructions,
  ]);

  const minNoticeErrorText = useMemo(() => {
    const numericNotice = +(noticeHours ?? 0);
    const numericNoticeMinutes = +(noticeMins ?? 0);

    const totalNoticeInMinutes = numericNotice * 60 + numericNoticeMinutes;

    return totalNoticeInMinutes >= MAX_NOTICE_IN_MINUTES ||
      numericNotice < 0 ||
      numericNoticeMinutes > 59
      ? 'Minimum notice must be between 0-99 hours and 0-59 minutes.'
      : '';
  }, [noticeHours, noticeMins]);

  const updateApproveShowingsOptions = (
    selection: ApproveShowingOption['value'],
  ) => {
    setShowingSettings(prev => ({
      ...prev,
      ...{ autoConfirm: selection === 'autoConfirm' },
      listingAgentConfirm: {
        enabled: selection === 'listingAgentConfirm',
        excludeFromQueue: false,
      },
    }));
  };

  const swalAutoConfirmOptions: SweetAlert.Settings &
    SweetAlert.AlertModalSettings = {
    title: 'Enable AUTO-CONFIRM',
    text: 'By enabling AUTO-CONFIRM, confirmation instructions may be automatically sent to the showing agent upon booking.\nThis also disables LA to confirm.\nAre you sure?',
    html: true,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Enable',
    closeOnConfirm: true,
    closeOnCancel: true,
  };

  const getApproveShowingValue = () => {
    if (showingSettings.autoConfirm) {
      return 'autoConfirm';
    }
    if (showingSettings.listingAgentConfirm.enabled) {
      return 'listingAgentConfirm';
    }
    return 'contactsToConfirm';
  };

  const handleApproveShowingsOptionsChange = (
    selection: ApproveShowingOption['value'],
  ) =>
    selection === 'autoConfirm'
      ? swal(
          swalAutoConfirmOptions,
          (confirmed: boolean) =>
            confirmed && updateApproveShowingsOptions(selection),
        )
      : updateApproveShowingsOptions(selection);

  useEffect(() => {
    const fetchShowingSettings = async () => {
      const settings = await getShowingDefaultSettings();
      setShowingSettings({
        ...showingSettings,
        ...settings,
        instructions: {
          ...showingSettings.instructions,
          ...settings.instructions,
        },
      });
    };

    fetchShowingSettings();
  }, []);
  return (
    <>
      <Title level={4}>Min. Showing Notice</Title>
      <StyledFlexColContainer>
        <label className="m-b display-flex align-items-center font-normal">
          <Radio
            data-cy="overnight-notice-hours"
            checked={!showingSettings.overnightNotice}
            onChange={() =>
              setShowingSettings(prev => ({
                ...prev,
                overnightNotice: false,
                ...(noticeHours || noticeMins ? {} : { notice: 1 }),
              }))
            }
          />
          <StyledInputNumber
            type="number"
            value={noticeHours ?? ''}
            disabled={showingSettings.overnightNotice}
            data-cy="overnight-notice-number-hours"
            onChange={(e: any) => {
              const notice = parseInt(e.target.value, 10);
              setNoticeHours(notice);
              setShowingSettings(prev => ({
                ...prev,
                notice,
              }));
            }}
            min={0}
            max={99}
          />
          (Hours)
          <StyledMinutesContainer>
            <StyledInputNumber
              type="number"
              value={noticeMins ?? ''}
              disabled={showingSettings.overnightNotice}
              data-cy="overnight-notice-number-minutes"
              onChange={(e: any) => {
                const noticeMinutes = parseInt(e.target.value, 10);
                setNoticeMins(noticeMinutes);
                setShowingSettings(prev => ({
                  ...prev,
                  noticeMinutes,
                }));
              }}
              min={0}
              max={59}
            />
            (Minutes)
          </StyledMinutesContainer>
        </label>
        {minNoticeErrorText && <StyledError>{minNoticeErrorText}</StyledError>}
        <Radio
          data-cy="overnight-notice-must-overnight"
          checked={showingSettings.overnightNotice}
          onChange={() =>
            setShowingSettings(prev => ({
              ...prev,
              overnightNotice: true,
            }))
          }
        >
          Overnight Notice Required
        </Radio>
      </StyledFlexColContainer>
      <Title level={4}>Max. Showing Duration</Title>
      <StyledContainer>
        <RadioGroup
          onChange={e =>
            setShowingSettings(prev => ({
              ...prev,
              maxDuration: e.target.value,
            }))
          }
          value={showingSettings.maxDuration}
        >
          {maxShowingDurationOptions.map(({ value, label }) => (
            <Radio
              key={value}
              value={value}
              data-cy={`max-duration-${value}`}
              checked={showingSettings.maxDuration === value}
            >{` ${label}`}</Radio>
          ))}
        </RadioGroup>
      </StyledContainer>
      <Title level={4}>Overlapping Showings</Title>
      <StyledFlexColContainer>
        <StyledSwitchGrid style={{ margin: '10px 0px' }}>
          <Switch
            checked={showingSettings.doubleBookings}
            onClick={() =>
              setShowingSettings(prev => ({
                ...prev,
                doubleBookings: !prev.doubleBookings,
                notifyOverlappingAppt: true,
                maxOverlap: -1,
                bufferSlots: 0,
              }))
            }
          />
          Allow Overlapping Showings?
        </StyledSwitchGrid>
        <StyledDropdown
          trigger={['click']}
          dropdownRender={() => (
            <Menu>
              {showingSettings.doubleBookings
                ? overlapOptions.map(({ value, label }) => (
                    <MenuItem
                      key={value}
                      onClick={() => {
                        setShowingSettings(prev => ({
                          ...prev,
                          maxOverlap: value,
                        }));
                      }}
                    >
                      {label}
                    </MenuItem>
                  ))
                : bufferOptions.map(({ value, label }) => (
                    <MenuItem
                      key={value}
                      onClick={() => {
                        setShowingSettings(prev => ({
                          ...prev,
                          bufferSlots: value,
                        }));
                      }}
                    >
                      {label}
                    </MenuItem>
                  ))}
            </Menu>
          )}
        >
          <StyledDropdownButton>
            {showingSettings.doubleBookings
              ? overlapOptions.find(
                  option => option.value === showingSettings.maxOverlap,
                )?.label || overlapOptions[0].label
              : bufferOptions.find(
                  option => option.value === showingSettings.bufferSlots,
                )?.label || bufferOptions[0].label}{' '}
            <DownOutlined />
          </StyledDropdownButton>
        </StyledDropdown>
        {showingSettings.doubleBookings && (
          <Checkbox
            type="checkbox"
            data-cy="notify-overlapping-appts"
            checked={showingSettings.notifyOverlappingAppt}
            onChange={() =>
              setShowingSettings(prev => ({
                ...prev,
                notifyOverlappingAppt: !prev.notifyOverlappingAppt,
              }))
            }
          >
            Notify agents regarding overlapping showings
          </Checkbox>
        )}
      </StyledFlexColContainer>
      <Title level={4}>Who should approve showings?</Title>
      <StyledFlexColContainer>
        <RadioGroup
          onChange={e => {
            handleApproveShowingsOptionsChange(
              e.target.value as ApproveShowingOption['value'],
            );
          }}
          value={getApproveShowingValue()}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {approveShowingsOptions.map(({ value, label }) => (
            <Radio
              checked={getApproveShowingValue() === value}
              value={value}
              key={value}
            >
              {label}
            </Radio>
          ))}
        </RadioGroup>
        {showingSettings.listingAgentConfirm?.enabled && (
          <StyledCheckBox
            type="checkbox"
            checked={showingSettings.listingAgentConfirm.excludeFromQueue}
            onChange={e => {
              const { checked } = e.target;
              setShowingSettings(prev => ({
                ...prev,
                listingAgentConfirm: {
                  ...prev.listingAgentConfirm,
                  excludeFromQueue: checked,
                },
              }));
            }}
            data-cy="hide-appt-from-queue"
          >
            Hide Showings From Queue
          </StyledCheckBox>
        )}
      </StyledFlexColContainer>
      <Title level={4}>Admin Instructions (Internal)</Title>
      <StyledFlexColContainer>
        <StyledTextArea
          placeholder="Showing instructions"
          data-cy="showing-instructions"
          value={showingSettings.showing_instructions}
          onChange={e => {
            const _showingInstructions = e.target.value;
            setShowingSettings(prev => ({
              ...prev,
              showing_instructions: _showingInstructions,
            }));
          }}
        />
        {showVendorInstructions && (
          <StyledTextArea
            placeholder="Vendor confirmation instructions"
            data-cy="vendor-confrm-instructions"
            value={showingSettings.vendor_instructions}
            onChange={e => {
              const _vendorInstructions = e.target.value;
              setShowingSettings(prev => ({
                ...prev,
                vendor_instructions: _vendorInstructions,
              }));
            }}
          />
        )}
        <div
          onClick={() => {
            setShowVendorInstructions(!showVendorInstructions);
            setShowingSettings(prev => ({
              ...prev,
              vendor_instructions: '',
            }));
          }}
        >
          <StyledToggleAddText toggle={showVendorInstructions.toString()}>
            {showVendorInstructions ? (
              <>
                <MinusOutlined style={{ color: `${theme.red6}` }} /> Remove
              </>
            ) : (
              <>
                <PlusOutlined style={{ color: `${theme.blue6}` }} /> Add
              </>
            )}{' '}
            Vendor Instructions
          </StyledToggleAddText>
        </div>
      </StyledFlexColContainer>
      <Title level={4}>Showing Agent Instructions</Title>
      <StyledFlexColContainer>
        {showingAgentInstructionsOptions.map(({ value, label }) => (
          <StyledCheckBox
            key={value}
            value={value}
            checked={showingSettings.instructions[value]}
            onChange={e => {
              const { checked } = e.target;
              setShowingSettings(prev => ({
                ...prev,
                instructions: {
                  ...prev.instructions,
                  [e.target.value]: checked,
                },
              }));
            }}
          >
            {label}
          </StyledCheckBox>
        ))}
        {showOtherInstructions && (
          <StyledTextArea
            placeholder="Other confirmation instructions"
            data-cy="other-confrm-instructions"
            value={showingSettings.instructions.confirmation?.details}
            onChange={e => {
              const _otherInstructions = e.target.value;
              setShowingSettings(prev => ({
                ...prev,
                instructions: {
                  ...prev.instructions,
                  confirmation: {
                    details: _otherInstructions,
                  },
                },
              }));
            }}
          />
        )}

        <div
          onClick={() => {
            setShowOtherInstructions(!showOtherInstructions);
            setShowingSettings(prev => ({
              ...prev,
              instructions: {
                ...prev.instructions,
                confirmation: { details: '' },
              },
            }));
          }}
        >
          <StyledToggleAddText toggle={showOtherInstructions.toString()}>
            {showOtherInstructions ? (
              <>
                <MinusOutlined style={{ color: `${theme.red6}` }} /> Remove
              </>
            ) : (
              <>
                <PlusOutlined style={{ color: `${theme.blue6}` }} /> Add
              </>
            )}{' '}
            Other Instructions
          </StyledToggleAddText>
        </div>
      </StyledFlexColContainer>
      <Title level={4}>Listing Contacts</Title>
    </>
  );
};

export default DefaultShowingSettings;

const StyledSwitchGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px 10px;
  margin: 20px 0px;
`;

const StyledFlexColContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: 0px 10px 10px 0px;
`;

const StyledContainer = styled.div`
  display: flex;
  margin: 0px 10px 10px 0px;
`;

const StyledInputNumber = styled(Input)`
  border: 1px solid ${theme.gray4};
  background: ${props => !props.disabled && theme.white} !important;
  width: 70px;
  margin: 0px 5px 10px 0px;
`;

const StyledMinutesContainer = styled.span`
  margin-left: 16px;
`;

const StyledDropdown = styled(Dropdown)`
  width: 100px;
`;

const StyledDropdownButton = styled.div`
  cursor: pointer;
  margin: 10px 0px;
  border-radius: 4px;
  padding: 5px 10px;
  border: 1px solid ${theme.gray4};
  align-items: center;
  display: flex;
  justify-content: space-between;
  /* width: 90%; */
`;

const StyledCheckBox = styled(Checkbox)`
  margin: 5px 8px;
`;

const StyledTextArea = styled(InputTextArea)`
  width: 335px;
  textarea.ant-input {
    height: 24px;
  }
  margin: 5px 0px;
`;

const StyledToggleAddText = styled<any>(Text)`
  margin-top: 10px;
  color: ${props => (props.toggle === 'true' ? theme.red6 : theme.blue6)};
  cursor: pointer;
`;

const StyledError = styled.label`
  margin: -5px 0px 5px 0;
  color: red;
`;
